import { loc } from '../../lib/format';
import { useEffect } from 'react';
import { SupportButton } from '../../components/SupportButton';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Trash2, Wallet, UserCheck } from 'lucide-react';
import { Button, Card, Notice, SectionTitle, SelectField, Skeleton, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { PhotoEditor } from '../../components/PhotoEditor';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { toForm } from '../../lib/media';
import { useAuth } from '../../stores/auth';
import { AccountSettings } from '../common/AccountSettings';
import { SafetySettings } from '../safety/SafetySettings';

interface Me {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  createdAt: string;
  customer: { id: string; preferences: Record<string, string> } | null;
}
interface Address {
  id: string;
  label: string;
  address: string;
}

const PREFS: Array<{ key: string; label: string; options: Array<[string, string]> }> = [
  { key: 'temperature', label: 'Aire acondicionado', options: [['', 'Sin preferencia'], ['cool', 'Fresco'], ['mild', 'Moderado'], ['off', 'Apagado']] },
  { key: 'music', label: 'Música', options: [['', 'Sin preferencia'], ['yes', 'Con música'], ['quiet', 'Sin música']] },
  { key: 'conversation', label: 'Conversación', options: [['', 'Sin preferencia'], ['chatty', 'Me gusta conversar'], ['quiet', 'Prefiero silencio']] },
];

export function CustomerProfile() {
  const qc = useQueryClient();
  const toast = useToast();
  const reloadUser = useAuth((s) => s.reloadUser);
  const welcome = new URLSearchParams(useLocation().search).has('welcome');
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/users/me') });
  const addresses = useQuery({ queryKey: ['addresses'], queryFn: () => api<Address[]>('/customers/me/addresses') });

  const form = useForm<{ firstName: string; lastName: string; phone: string }>();
  useEffect(() => {
    if (me.data) form.reset({ firstName: me.data.firstName, lastName: me.data.lastName, phone: me.data.phone ?? '' });
  }, [me.data, form]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    await reloadUser();
  };

  const save = form.handleSubmit(async (v) => {
    try {
      await api('/users/me', { method: 'PATCH', body: v });
      await refresh();
      toast('Perfil actualizado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  });

  const prefs = useMutation({
    mutationFn: (preferences: Record<string, string>) => api('/customers/me/preferences', { method: 'PUT', body: { preferences } }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ['me'] }), toast('Preferencias guardadas', 'success')),
    onError: (e) => toast(errorMessage(e), 'danger'),
  });

  const delAddress = useMutation({
    mutationFn: (id: string) => api(`/customers/me/addresses/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
    onError: (e) => toast(errorMessage(e), 'danger'),
  });

  const d = me.data;
  return (
    <Page title="Mi perfil" onRefresh={() => Promise.all([me.refetch(), addresses.refetch()])}>
      <div className="flex flex-col gap-6">
        {welcome && <Notice>¡Bienvenido! Agrega una foto para que tu conductor te identifique fácilmente al recogerte.</Notice>}

        <Card className="p-5">
          {!d ? (
            <Skeleton className="h-24" />
          ) : (
            <PhotoEditor
              src={d.avatarUrl}
              firstName={d.firstName}
              lastName={d.lastName}
              onUpload={async (blob) => (await api('/users/me/avatar', { method: 'PUT', form: toForm(blob, {}, 'perfil') }), refresh())}
              onRemove={async () => (await api('/users/me/avatar', { method: 'DELETE' }), refresh())}
            />
          )}
          <p className="mt-3 text-xs text-[var(--text-muted)]">Tu foto y tu nombre solo los ve el conductor asignado a tus viajes.</p>
        </Card>

        <Card className="p-5">
          <SectionTitle title="Datos personales" subtitle={d ? `Cliente desde ${new Date(d.createdAt).toLocaleDateString(loc, { month: 'long', year: 'numeric' })}` : undefined} />
          <form onSubmit={save} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Nombre" {...form.register('firstName', { required: true, minLength: 2 })} />
              <TextField label="Apellido" {...form.register('lastName', { required: true, minLength: 2 })} />
            </div>
            <TextField label="Teléfono" type="tel" inputMode="tel" {...form.register('phone')} />
            <TextField label="Correo electrónico" value={d?.email ?? ''} disabled readOnly />
            <Button type="submit" loading={form.formState.isSubmitting}>Guardar cambios</Button>
          </form>
        </Card>

        <Card className="p-5">
          <SectionTitle title="Preferencias de viaje" subtitle="Tu conductor las verá en cada servicio." />
          <div className="flex flex-col gap-3">
            {PREFS.map((p) => (
              <SelectField
                key={p.key}
                label={p.label}
                value={d?.customer?.preferences[p.key] ?? ''}
                disabled={prefs.isPending}
                onChange={(e) => prefs.mutate({ ...(d?.customer?.preferences ?? {}), [p.key]: e.target.value })}
              >
                {p.options.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </SelectField>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle title="Direcciones favoritas" subtitle="Se guardan al solicitar un viaje." />
          {addresses.data?.length ? (
            <ul className="divide-y divide-[var(--border)]">
              {addresses.data.map((a) => (
                <li key={a.id} className="flex items-center gap-3 py-3">
                  <MapPin className="size-5 text-brand-red shrink-0" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{a.label}</p>
                    <p className="text-sm text-[var(--text-muted)] truncate">{a.address}</p>
                  </div>
                  <button className="tap flex items-center justify-center text-[var(--text-muted)] hover:text-brand-red" aria-label={`Eliminar ${a.label}`} onClick={() => delAddress.mutate(a.id)}>
                    <Trash2 className="size-5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">Aún no tienes direcciones guardadas.</p>
          )}
        </Card>

        <Link to="/app/my-driver">
          <Card className="p-4 flex items-center gap-3">
            <UserCheck className="size-5 text-[var(--text-muted)]" aria-hidden />
            <span className="flex-1 font-medium">Mi conductor</span>
          </Card>
        </Link>

        <Link to="/app/payments">
          <Card className="p-4 flex items-center gap-3">
            <Wallet className="size-5 text-[var(--text-muted)]" aria-hidden />
            <span className="flex-1 font-medium">Pagos y comprobantes</span>
          </Card>
        </Link>

        <SupportButton base="/app" />
        <SafetySettings customer />
        <AccountSettings />
      </div>
    </Page>
  );
}
